import React, { useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  Box,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Modal,
  IconButton,
  Chip,
} from "@mui/material";
import { Search as SearchIcon, Edit as EditIcon } from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import baseURL from "../../config";


const SignOffModal = ({ signoffDetails, onUpdate }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedSignOff, setSelectedSignOff] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [formData, setFormData] = useState({
    date: "",
    portName: "",
    portCountry: "",
  });

  const userID = localStorage.getItem("user id");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCrewName, setSelectedCrewName] = useState("");



  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value.toLowerCase());
  };

  const handleEditClick = (contract, crewName) => {
    setSelectedSignOff(contract);
    setFormData({
      date: "",
      portName: "",
      portCountry: "",
    });
    setSelectedCrewName(crewName);
    setEditModalOpen(true);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const getContractRemainingDays = (startDateStr, durationInMonths) => {
    if (!startDateStr || !durationInMonths) return null;

    const startDate = new Date(startDateStr);
    const endDate = new Date(startDate);
    endDate.setMonth(endDate.getMonth() + parseInt(durationInMonths));

    const today = new Date();

    if (endDate <= today) return "Contract Ended";

    let remainingMonths = (endDate.getFullYear() - today.getFullYear()) * 12 +
      (endDate.getMonth() - today.getMonth());

    let adjustedEnd = new Date(today);
    adjustedEnd.setMonth(adjustedEnd.getMonth() + remainingMonths);

    // If adjustedEnd goes past the actual endDate, reduce a month
    if (adjustedEnd > endDate) {
      remainingMonths--;
      adjustedEnd.setMonth(adjustedEnd.getMonth() - 1);
    }

    // Now calculate the remaining days
    const remainingDays = Math.ceil((endDate - adjustedEnd) / (1000 * 60 * 60 * 24));

    const months = remainingMonths;
    const days = remainingDays;

    let result = [];
    if (months) result.push(`${months} month${months > 1 ? "s" : ""}`);
    if (days) result.push(`${days} day${days > 1 ? "s" : ""}`);

    return `Contract remaining : ${result.join(" ")} `;
  };

  const handleSave = async () => {

    if (!formData.date || !formData.portName.trim() || !formData.portCountry.trim()) {
      toast.error("All fields are required.");
      return;
    }


    if (!selectedSignOff) {
      alert("No contract selected!");
      return;
    }

    setIsSaving(true);

    const token = localStorage.getItem("token");

    const dataToSend = {
      creatorId: userID,
      crewId: selectedSignOff.contractDetails?.crewdetails?._id || "",
      contractId: selectedSignOff.contractDetails?._id || "",
      departure: [{
        date: formData.date,
        portName: formData.portName,
        country: formData.portCountry,
      }]
    };

    try {
      const response = await axios.post(`${baseURL}/api/statusAfterForm1`, dataToSend, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      toast.success("Data saved successfully!");
      setEditModalOpen(false);
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("Error saving data:", error.response?.data || error.message);
      alert("Failed to save data. Please try again.");
    } finally {
      setIsSaving(false); // Hide loader
    }
  };

  const handleOpenSignOffFiles = (signOffData) => {
    const documents = [];

    if (signOffData.cdcStamppageFile?.filePath) {
      documents.push({
        name: "CDC Stamp Page",
        filePath: `${baseURL}/${signOffData.cdcStamppageFile.filePath.replace(/\\/g, "/")}`,
      });
    }

    if (signOffData.passport?.filePath) {
      documents.push({
        name: "Passport",
        filePath: `${baseURL}/${signOffData.passport.filePath.replace(/\\/g, "/")}`,
      });
    }

    if (signOffData.salaryclose?.filePath) {
      documents.push({
        name: "salaryclose",
        filePath: `${baseURL}/${signOffData.salaryclose.filePath.replace(/\\/g, "/")}`,
      });
    }

    setSelectedDocuments(documents);
    setOpenModal(true);
  };


  const filteredSignOffDetails = Array.isArray(signoffDetails)
    ? signoffDetails.filter((contract) => {
      const vesselName =
        contract.contractDetails?.ShipDetails?.vesselname?.toLowerCase() || "";
      const agentName =
        contract.contractDetails?.crewingAgent?.agentName?.toLowerCase() || "";
      return vesselName.includes(searchQuery) || agentName.includes(searchQuery);
    })
    : [];

  return (
    <Box mt={3} width="100%" p={2}>
      <Box
        sx={{
          p: 1,
          bgcolor: "#C30B12",
          color: "white",
          display: "flex",
          alignItems: "center",
          position: "relative",
        }}
      >
        <TextField
          placeholder="Search by Vessel or Agent Name"
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="primary" />
              </InputAdornment>
            ),
          }}
          sx={{
            bgcolor: "#f5f5f5",
            borderRadius: 2,
            width: "280px",
          }}
        />

        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "nowrap",
          }}
        >
          Sign-Off Details
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 2, mt: 1 }}>
        {filteredSignOffDetails.length > 0 ? (
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{ bgcolor: "#f5f5f5" }}>
                <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "bold" }}>Sr. No.</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Contract Details</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Documents</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSignOffDetails.map((contract, index) => {
                const cocDate = contract.contractStatusAfterForm1?.coc?.[0]?.Date;
                const duration = contract.contractDetails?.contractDuration;
                const contractRemainingLabel = getContractRemainingDays(cocDate, duration);

                return (
                  <TableRow key={index} hover>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight="bold">
                        <strong style={{ color: "black" }}>Vessel Name:</strong> {contract.contractDetails?.ShipDetails?.vesselname || "N/A"}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", marginTop: 1 }}>
                        <Chip
                          size="small"
                          label={`Crew: ${contract.contractDetails?.crewdetails?.name || "N/A"}`}
                          sx={{ bgcolor: "#e3f2fd", color: "#0d47a1" }}
                        />
                        <Chip
                          size="small"
                          label={`Indos No.: ${contract.contractDetails?.crewdetails?.indosno || "N/A"}`}
                          sx={{ bgcolor: "#fff9c4", color: "#f57f17" }}
                        />
                        <Chip
                          size="small"
                          label={`Agent: ${contract.contractDetails?.crewingAgent?.agentName || "N/A"}`}
                          sx={{ bgcolor: "#e8f5e9", color: "#1b5e20" }}
                        />

                        <Chip
                          size="small"
                          label={`Port Country: ${contract.contractStatusAfterForm1?.signOff?.[0]?.portCountry || "N/A"}`}
                          sx={{ bgcolor: "#ede7f6", color: "#311b92" }}
                        />
                        <Chip
                          size="small"
                          label={`Port Name: ${contract.contractStatusAfterForm1?.signOff?.[0]?.portName || "N/A"}`}
                          sx={{ bgcolor: "#e3f2fd", color: "#0d47a1" }}
                        />
                        <Chip
                          size="small"
                          label={`SignOff Date: ${contract.contractStatusAfterForm1?.signOff?.[0]?.date
                            ? new Date(contract.contractStatusAfterForm1.signOff[0].date).toLocaleDateString("en-GB", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                            : "N/A"
                            }`}
                          sx={{ bgcolor: "#fff3e0", color: "#e65100" }}
                        />

                        {contractRemainingLabel && (
                          <Chip
                            size="medium"
                            icon={
                              contractRemainingLabel === "Contract Ended" ? (
                                <WarningIcon sx={{ color: "#b71c1c" }} />
                              ) : (
                                <HourglassEmptyIcon sx={{ color: "#0277bd" }} />
                              )
                            }
                            label={contractRemainingLabel}
                            sx={{
                              bgcolor: contractRemainingLabel === "Contract Ended" ? "#ffebee" : "#e1f5fe",
                              color: contractRemainingLabel === "Contract Ended" ? "#b71c1c" : "#01579b",
                              fontWeight: "bold",
                              borderRadius: 2,
                              px: 2,
                              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
                              userSelect: "none",
                              minWidth: 180,
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          />
                        )}

                      </Box>
                    </TableCell>
                    <TableCell>
                      {contract.contractStatusAfterForm1?.signOff?.[0] && (
                        <>
                          {(contract.contractStatusAfterForm1.signOff[0].cdcStamppageFile?.filePath ||
                            contract.contractStatusAfterForm1.signOff[0].passport?.filePath ||
                            contract.contractStatusAfterForm1.signOff[0].salaryclose?.filePath) && (
                              <IconButton
                                onClick={() =>
                                  handleOpenSignOffFiles(contract.contractStatusAfterForm1.signOff[0])
                                }
                              >
                                <PictureAsPdfIcon sx={{ color: "red" }} />
                              </IconButton>
                            )}
                        </>
                      )}
                    </TableCell>

                    <TableCell>
                      <IconButton color="primary" onClick={() => handleEditClick(contract, contract.contractDetails?.crewdetails?.name || "N/A")}>
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>

          </Table>
        ) : (
          <Typography color="textSecondary" align="center">
            No matching sign-off details found.
          </Typography>
        )}
      </Paper>

      {/* Modal for Editing Sign-Off Details */}
      <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <Paper
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            p: 3,
            borderRadius: 2,
            border: "none",
          }}
        >
          <Typography variant="h6" align="center" gutterBottom>
            Enter Arrival: {" "}
            {selectedCrewName && (
              <span style={{ color: "green" }}> {selectedCrewName}</span>
            )}
          </Typography>
          <TextField
            fullWidth
            required
            label="Date"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            margin="dense"
            variant="outlined"
            inputProps={{
              max: new Date().toISOString().split("T")[0], // Restricts to past & present dates
            }}
          />
          <TextField
            fullWidth
            required
            label="Port Name"
            name="portName"
            value={formData.portName}
            onChange={handleChange}
            margin="dense"
            variant="outlined"
          />
          <TextField
            fullWidth
            required
            label="Country"
            name="portCountry"
            value={formData.portCountry}
            onChange={handleChange}
            margin="dense"
            variant="outlined"
          />
          <Box mt={2} display="flex" justifyContent="space-between">
            <Button onClick={() => setEditModalOpen(false)} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>

          </Box>
        </Paper>
      </Modal>

      {/* Modal for documents Details */}
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: "60%", md: "50%" },
            maxHeight: "80vh",
            bgcolor: "white",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            overflowY: "auto",
          }}
        >
          <Typography
            variant="h6"
            gutterBottom
            sx={{ textAlign: "center", mb: 2, fontWeight: "bold" }}
          >
            Sign-On Documents
          </Typography>

          {selectedDocuments.length > 0 ? (
            selectedDocuments.map((doc, index) => (
              <Paper
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 2,
                  mb: 2,
                  boxShadow: 2,
                  borderRadius: 2,
                  transition: "0.3s",
                  "&:hover": { boxShadow: 4, bgcolor: "#f5f5f5" },
                }}
              >
                <Box display="flex" alignItems="center">
                  <PictureAsPdfIcon sx={{ color: "red", mr: 1 }} />
                  <Typography variant="body1">
                    {doc.name || "Unnamed Document"}
                  </Typography>
                </Box>

                {doc.filePath && doc.filePath.trim() !== "" ? (
                  <Button
                    variant="contained"
                    color="primary"
                    href={doc.filePath}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    Download
                  </Button>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    No File Available
                  </Typography>
                )}
              </Paper>
            ))
          ) : (
            <Typography align="center" color="textSecondary">
              No documents available
            </Typography>
          )}

          <Box display="flex" justifyContent="center" mt={2}>
            <Button variant="contained" color="error" onClick={() => setOpenModal(false)}>
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <ToastContainer />
    </Box>
  );
};

export default SignOffModal;
